import { useTranslation } from "react-i18next";
import type { SupportedLanguage } from "../../interfaces";
import type {
  CongressCardProps,
  CongressCountdownCopy,
} from "../ui/CongressCard";
import { Button } from "../ui/Button";
import type { CongressStatus } from "../../domains/congress/congressStatus";
import { formatCongressDay } from "../../domains/congress/congressDateLabel";
import { DesktopCongressCountdown } from "./DesktopCongressCountdown";
import { DesktopCongressCover } from "./DesktopCongressCover";
import { DesktopCongressLiveBadge } from "./DesktopCongressLiveBadge";

/**
 * A congress at the top of its Room on desktop, above the tabs (Figma
 * "DesktopCongressBanner"): the artwork, who shares the space, the name and
 * dates, and where the congress stands, with the way into the space page.
 * Never an item of the Latest list.
 */
export function DesktopCongressBanner({
  name,
  dateLabel,
  cover,
  coverColor,
  status,
  organisationName,
  accentColor,
  contentLang,
  countdownCopy,
  onOpen,
}: {
  name: string;
  dateLabel: string;
  cover: Extract<CongressCardProps["display"], { variant: "full" }>["cover"];
  coverColor: string;
  status: CongressStatus;
  organisationName: string;
  /** The partner's colour, for the attribution line */
  accentColor: string;
  contentLang: SupportedLanguage;
  countdownCopy: CongressCountdownCopy;
  onOpen: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-5 rounded-2xl border border-ds-slate-100 py-3 pr-5 pl-3 font-dm-sans"
      style={{
        backgroundColor: `color-mix(in srgb, ${coverColor} 12%, transparent)`,
      }}
    >
      <DesktopCongressCover
        cover={cover}
        coverColor={coverColor}
        name={name}
        className="h-26 w-58 rounded-xl"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p
          className="m-0 text-desktop-meta font-semibold"
          style={{ color: accentColor }}
        >
          {t("Congress space · Shared by {{organisationName}}", {
            organisationName,
          })}
        </p>
        <p className="m-0 text-heading-xs font-semibold text-ds-black">
          {name}
        </p>
        <p className="m-0 text-footnote text-ds-slate-600">{dateLabel}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2.5">
        <BannerStatus
          status={status}
          contentLang={contentLang}
          countdownCopy={countdownCopy}
        />
        <Button variant="primary" size="sm" onClick={onOpen}>
          {t("Open congress space")}
        </Button>
      </div>
    </div>
  );
}

function BannerStatus({
  status,
  contentLang,
  countdownCopy,
}: {
  status: CongressStatus;
  contentLang: SupportedLanguage;
  countdownCopy: CongressCountdownCopy;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  switch (status.kind) {
    case "countdown":
      return (
        <>
          <p className="m-0 text-caption font-bold tracking-wider text-ds-slate-500 uppercase">
            {t("Starts in")}
          </p>
          <DesktopCongressCountdown
            countdown={status.countdown}
            copy={countdownCopy}
          />
        </>
      );
    case "live":
      return (
        <div className="flex items-center gap-2">
          <span className="text-footnote text-ds-slate-600">
            {t("Day {{day}} of {{total}}", {
              day: status.day,
              total: status.dayCount,
            })}
          </span>
          <DesktopCongressLiveBadge label={countdownCopy.liveNow} />
        </div>
      );
    case "ended":
      return status.catchUpUntil === null ? null : (
        <p className="m-0 text-footnote text-ds-slate-600">
          {t("Catch up until {{day}}", {
            day: formatCongressDay(status.catchUpUntil, contentLang),
          })}
        </p>
      );
    case "upcoming":
    case "evergreen":
      return null;
    default:
      throw new Error(status satisfies never);
  }
}
